
  import Events from '@/events/Events';
  import {shuffleArray} from '@/helper/ArrayHelper';
  import JobBoxAlert from '@/components/JobBoxAlert';
  import {prepareGeoApifyReverseSearchUrl} from '@/helper/UrlHelper';
  import JobBoxWithTypesense from '@/mixins/JobBoxWithTypesense';
  import axios from 'axios';

  export default {
    name: 'SectionJobBoxWithoutTabs',
    components: {
      Pagination: () => import('@/components/Pagination'),
      JobBoxAlert,
    },
    mixins: [JobBoxWithTypesense],
    props: {
      section: {
        type: Object,
        required: false,
        default: () => {},
      },
    },
    data() {
      return {
        page: 1,
        jobPostings: [],
        numJobPostings: this.section.num_jobs,
        cacheKey: '',
        jobAlertSearchRequestUrl: '',
        whereInput: '',
        boxType: '',
      };
    },
    async fetch() {
      const filterParams = new URLSearchParams();
      filterParams.set('itemsPerPage', '50');

      this.boxType = this.$t(
        'job.box_type_' + this.section.box_type.toLowerCase()
      );

      // TopTeaser und InstantTeaser sollen hier immer zu den Filter-Bedingungen passen
      this.filterConfig.filteredTopTeaser = true;
      this.filterConfig.filteredInstantTeaser = true;
      this.filterConfig.filteredHomepageTeaser = true;
      this.filterConfig.numJobPostings = this.numJobPostings * 10;
      this.filterConfig.shuffleAndSlice = this.numJobPostings * 10;

      // Unterscheidung welche Jobs ermittelt werden sollen
      if (this.section.box_type === 'TopOffers') {
        await this.getJobPostingsWithAdditionalFilter('top-offers');
      } else if (this.section.box_type === 'Opportunities') {
        await this.getJobPostingsWithAdditionalFilter('opportunities');
      } else if (this.section.box_type === 'NewJobs') {
        await this.getNewJobPostings();
      } else if (
        this.section.box_type === 'Recommendations' &&
        this.$auth.loggedIn
      ) {
        await this.getRecommendationJobPostings(filterParams);
      } else {
        await this.getDefaultJobPostings();
      }

      // Shuffe oder nicht? Ggf. Setting in Section? Oder per Default nach
      if (this.jobPostings.length > 0) {
        this.jobPostings = shuffleArray(this.jobPostings);
      }

      filterParams.delete('itemsPerPage');
      filterParams.delete('isPublished');

      if (this.section.box_type === 'Recommendations' && this.$auth.loggedIn) {
        const jobAlertRecommendationParams = new URLSearchParams();
        if (this.$auth.user.candidateProfile.preferredSalary) {
          jobAlertRecommendationParams.append(
            'salary[min]',
            this.$auth.user.candidateProfile.preferredSalary
          );
        }
        if (this.$auth.user.candidateProfile.preferredActivities) {
          Object.values(this.$auth.user.candidateProfile.preferredActivities)
            .slice(0, -1)
            .forEach((activity) => {
              jobAlertRecommendationParams.append('activity[]', activity);
            });
        }
        if (this.$auth.user.candidateProfile.addresses) {
          Object.values(this.$auth.user.candidateProfile.addresses)
            .slice(0, -1)
            .some(async (address) => {
              await this.getGeoLocation(address.latitude, address.longitude);
              if (address.geoCluster && this.whereInput) {
                jobAlertRecommendationParams.append(
                  'distance',
                  `${address.latitude},${address.longitude},25000`
                );
                jobAlertRecommendationParams.append(
                  'whereInput',
                  this.whereInput
                );
                return true;
              }
            });
        }
        this.jobAlertSearchRequestUrl =
          decodeURI(jobAlertRecommendationParams.toString()) +
          '&order[order_number]=desc&itemsPerPage=16&page=1';
      } else if (this.section.box_type != 'Recommendations') {
        if (
          this.section.filterParams &&
          this.section.filterParams.latitude &&
          this.section.filterParams.longitude &&
          this.section.filterParams.distance
        ) {
          await this.getGeoLocation(
            this.section.filterParams.latitude,
            this.section.filterParams.longitude
          );
          if (this.whereInput) {
            filterParams.append('whereInput', this.whereInput);
          } else {
            filterParams.delete('distance');
          }
        }
        let jobAlertSearchRequestUrl =
          decodeURI(filterParams.toString()) +
          '&order[order_number]=desc&itemsPerPage=16&page=1';
        if (this.section.box_type === 'TopOffers') {
          jobAlertSearchRequestUrl =
            jobAlertSearchRequestUrl + '&additionalFilters=top-offers';
        }
        if (this.section.box_type === 'Opportunities') {
          jobAlertSearchRequestUrl =
            jobAlertSearchRequestUrl + '&additionalFilters=opportunities';
        }
        this.jobAlertSearchRequestUrl = jobAlertSearchRequestUrl;
      }
      if (!this.jobAlertSearchRequestUrl.includes('isPublished')) {
        this.jobAlertSearchRequestUrl += '&isPublished=1';
      }
    },
    computed: {
      visibleJobs() {
        const jobs = this.jobPostings.slice(
          (this.page - 1) * this.numJobPostings,
          this.page * this.numJobPostings
        );
        let items = [];
        if (process.client && this.jobPostings) {
          items = this.jobPostings.map((item, key) => ({
            id: item.id,
            position: key + 1,
          }));
          this.$emitWithClientData(Events.JobBoxViewed, {
            boxType: this.section.box_type,
            items,
          });
        }
        return jobs;
      },
    },
    methods: {
      async getJobPostingsWithAdditionalFilter(
        additional_filter = 'top-offers'
      ) {
        // Jobs mit Top-Job/Opportunity-Filter und SpecialOffers ermitteln
        this.filterConfig.additionalFilterParams.set(
          'additionalFilters',
          additional_filter
        );

        this.filterConfig.filteredTopTeaser = true;
        this.filterConfig.filteredInstantTeaser = true;
        this.filterConfig.filteredHomepageTeaser = true;

        await this.fetchJobPostings();

        // Weiteres Auffüllen der Jobs mit Jobs mit Kriterien aus Filter-Params der Section die keine Top-Jobs sind
        if (this.mergedJobPostings.length < this.numJobPostings) {
          // Keine TopJobs
          this.filterConfig.additionalFilterParams = new URLSearchParams();

          // Hier sollen nur die normalen Jobs berücksichtigt werden und nicht mehr die TopTeaser/InstantTeaser
          // weil diese sonst mehrfach einfließen
          this.filterConfig.getTopTeaserJobPostings = false;
          this.filterConfig.getInstantTeaserJobPostings = false;
          this.filterConfig.getHomepageTeaserJobPostings = false;

          await this.fetchJobPostings();

          // Müsste hier nicht noch sichergestellt werden dass dieser Job nicht bereits in der Menge ist????
          this.mergedJobPostings.forEach((jobPosting) => {
            if (this.jobPostings.length <= this.numJobPostings) {
              this.jobPostings.push(jobPosting);
            }
          });
        }

        this.jobPostings = this.mergedJobPostings;
      },
      async getDefaultJobPostings() {
        // Jobs mit SpecialOffers, ohne Bewerbungen
        this.excludeNotYetAppliedTo = true;

        await this.fetchJobPostings();
        this.jobPostings = this.mergedJobPostings;
      },
      async getNewJobPostings() {
        // Jobs mit der letzten 24 Stunden
        this.filterConfig.additionalFilterParams.set('created', '86400');

        await this.fetchJobPostings();

        // Weiteres Auffüllen der Jobs mit Jobs der letzten 7 Tage
        if (this.mergedJobPostings.length < this.numJobPostings) {
          // Jobs der letzten 7 Tage, keine SpecialOffers
          this.filterConfig.additionalFilterParams.set('created', '604800');

          await this.fetchJobPostings();
        }

        this.jobPostings = this.mergedJobPostings;
      },
      async getRecommendationJobPostings(filterParams) {
        this.jobPostings = await this.$store
          .dispatch('api/jobPosting/getJobPostingRecommendations', filterParams)
          .then((x) => x.data);

        // Wenn zu wenig Jobs: Recommendations ohne Filter-Kriterien werden angehängt
        if (this.jobPostings.length < this.numJobPostings) {
          const allRecommendationJobPostings = await this.$store
            .dispatch('api/jobPosting/getJobPostingRecommendations')
            .then((x) => x.data);

          // Müsste hier nicht noch sichergestellt werden dass dieser Job nicht bereits in der Menge ist????
          allRecommendationJobPostings.forEach((jobPosting) => {
            if (this.jobPostings.length <= this.numJobPostings) {
              this.jobPostings.push(jobPosting);
            }
          });
        }
      },
      async getGeoLocation(lat, lon) {
        await axios
          .get(prepareGeoApifyReverseSearchUrl(lat, lon))
          .then((result) => {
            if (
              result &&
              result.data &&
              result.data.features &&
              result.data.features[0]
            ) {
              this.whereInput =
                result.data.features[0].properties['address_line1'] +
                ',' +
                result.data.features[0].properties['address_line2'];
            }
          })
          .catch({});
      },
    },
  };
